'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { cn } from '../utils/cn';

interface VirtualGridProps<T> {
  items: T[];
  scrollRef: RefObject<HTMLDivElement | null>;
  rowHeight: number;
  gap: number;
  minColumnWidth: number;
  renderItem: (item: T, index: number) => ReactNode;
  footer?: ReactNode;
  className?: string;
  overscanRows?: number;
  innerRef?: RefObject<HTMLDivElement | null>;
}

export function VirtualGrid<T>({
  items,
  scrollRef,
  rowHeight,
  gap,
  minColumnWidth,
  renderItem,
  footer,
  className,
  overscanRows = 3,
  innerRef,
}: VirtualGridProps<T>): ReactNode {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [columns, setColumns] = useState(1);
  const [range, setRange] = useState({ start: 0, end: 0 });

  const rowStride = rowHeight + gap;
  const rowCount = Math.ceil(items.length / columns);

  const measureColumns = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const width = grid.clientWidth;
    if (width <= 0) return;
    setColumns(Math.max(1, Math.floor((width + gap) / (minColumnWidth + gap))));
  }, [gap, minColumnWidth]);

  // WHY: колонки меряются в ref-колбэке (коммит), до layout-эффекта восстановления скролла — иначе scrollTop уезжает глубже
  // и клампится
  const attachGrid = useCallback(
    (node: HTMLDivElement | null) => {
      gridRef.current = node;
      if (innerRef) innerRef.current = node;
      if (node) measureColumns();
    },
    [innerRef, measureColumns],
  );

  useLayoutEffect(() => {
    measureColumns();
    const grid = gridRef.current;
    if (!grid || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measureColumns);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [measureColumns]);

  const recalc = useCallback(() => {
    const grid = gridRef.current;
    const scroller = scrollRef.current;
    if (!grid || !scroller) return;

    const offsetTop = grid.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    const viewportTop = scroller.scrollTop - offsetTop;
    const firstRow = Math.floor(viewportTop / rowStride) - overscanRows;
    const lastRow = Math.ceil((viewportTop + scroller.clientHeight) / rowStride) + overscanRows;

    const start = Math.max(0, firstRow) * columns;
    const end = Math.min(items.length, Math.max(0, lastRow) * columns);
    setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, [scrollRef, rowStride, columns, items.length, overscanRows]);

  useEffect(() => {
    recalc();
    const scroller = scrollRef.current;
    if (!scroller) return;

    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        recalc();
      });
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [recalc, scrollRef]);

  const startRow = Math.floor(range.start / columns);
  const endRow = Math.ceil(range.end / columns);
  const topSpacer = startRow * rowStride;
  const bottomSpacer = Math.max(0, (rowCount - endRow) * rowStride);
  const visible = items.slice(range.start, range.end);

  return (
    <div
      ref={attachGrid}
      className={cn('grid items-start', className)}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap }}
    >
      {topSpacer > 0 && <div aria-hidden style={{ gridColumn: '1 / -1', height: topSpacer }} />}
      {visible.map((item, i) => renderItem(item, range.start + i))}
      {bottomSpacer > 0 && <div aria-hidden style={{ gridColumn: '1 / -1', height: bottomSpacer }} />}
      {footer && <div style={{ gridColumn: '1 / -1' }}>{footer}</div>}
    </div>
  );
}
